import React from 'react';
import parse from 'html-react-parser';
import { graphql, PageProps, Script, Link } from 'gatsby';
import { imageWithPrefix, scrollTo, addApostropheS } from '@ecs/tng-common/src/scripts/helpers';
import {
  advertiserDisclosure,
  completeInfo,
  opinionsReviewsDisclosure,
  prosConsDisclosure
} from '@ecs/tng-common/src/documents/disclosures/cm-disclosures';
import { ficoShort } from '@ecs/tng-common/src/documents/disclosures/regulatory';
import ArticlesSlider, {
  IArticle
} from '@ecs/tng-common/src/components/articles-slider/ArticlesSlider';
import DisclosureWithSymbol from '@ecs/tng-common/src/components/disclosure-with-symbol/DisclosureWithSymbol';
import Image from '@ecs/tng-common/src/components/image/Image';
import ReviewStars from '@ecs/tng-common/src/components/review-stars/ReviewStars';
import SEO from '@ecs/tng-common/src/components/seo/SEO';
import TooltipExp from '@ecs/tng-common/src/components/tooltip/TooltipExp';
import FicoScoreRange from '@ecs/tng-common/src/components/fico-score-range/FicoScoreRange';
import { Accordion } from 'react-bootstrap';
import IconsHolder from '@ecs/tng-common/src/components/icons-holder/IconsHolder';
import LayoutFull from '../components/LayoutFull';
import PRsingleReview from '../components/pr-single-review';
import CardsSuppressedModal from '../components/cards-suppressed-modal';
import CardsApplyButton from '../components/cards-apply-button';
import CardsApplyLink from '../components/cards-apply-link';
import {
  ifSuppressedOffer,
  suppressionValues
} from '../../node-actions/create-nodes/shared/helpers';
import { superscriptRegisterMark } from '../../../tng-common/src/scripts/helpers';
import { ICard } from '../interfaces/cards';
// styles
import '../styles/card-details.scss';
import { DescrCategories } from '../scripts/cards';

type CardDetailsTemplateProps = {
  allCards: {
    nodes: ICard[];
  };
  allArticles: {
    nodes: IArticle[];
  };
};
type pageContextType = {
  offerId: string;
};
function CardDetailsTemplate({ data }: PageProps<CardDetailsTemplateProps, pageContextType>) {
  const allArticles = data?.allArticles?.nodes;
  const card = data?.allCards?.nodes[0];
  const returnBulletValue = (value: string) => {
    return value && value !== 'N/A' ? value : 'N/A*';
  };
  const ifToShowApplyLink = () => {
    return ifSuppressedOffer(card.suppressedViews, suppressionValues.speedbump);
  };
  return (
    <LayoutFull mbox="geo">
      <section className="details">
        <div className="container container-sm-py">
          <div className="row justify-content-xl-center">
            <div className="col-xl-11 content-mb text-center">
              <h1>{parse(card.offerNameSuperscripted)}</h1>
              {card?.provider && (
                <p className="h4 mb-0">{parse(card.provider)} is an Experian Partner</p>
              )}
            </div>
            <div className="col-12 col-xl-10">
              <TooltipExp
                id="adv-id"
                body={advertiserDisclosure}>
                <button
                  type="button"
                  className="btn btn-link text-decoration-underline-hover d-block ms-auto mb-3 body-text-sm">
                  Advertiser Disclosure
                </button>
              </TooltipExp>
              <div className="bg-white border rounded-3 p-3 p-lg-4">
                <div className="d-lg-flex align-items-stretch">
                  <Image
                    src={card.imageDownloaded}
                    alt={`${card.offerName} logo.`}
                    width="240"
                    height="95"
                    className="img-fluid"
                  />
                  <div className="flex-fill px-lg-4 d-flex flex-wrap d-lg-block">
                    <p className="h4 my-4 my-lg-0 order-2 w-100">
                      {parse(card.offerNameSuperscripted)}
                    </p>
                    {card.reviews && card.reviews.average_rating && (
                      <ReviewStars
                        onClick={() => {
                          scrollTo('single-review');
                        }}
                        as="button"
                        className="mt-3"
                        rollup={card.reviews}
                      />
                    )}
                  </div>
                  <div className="col-lg-3 d-flex justify-content-center flex-wrap text-center">
                    {!card.suppressedViews.includes(suppressionValues.public) &&
                      (ifToShowApplyLink() ? (
                        <CardsApplyLink
                          className="btn btn-secondary w-100 align-self-start"
                          analyticsPlacement="top-details-page"
                          card={card}
                          text="Apply"
                          index={0}
                        />
                      ) : (
                        <CardsApplyButton
                          className="btn btn-secondary w-100 align-self-start"
                          analyticsPlacement="top-details-page"
                          card={card}
                          text="Apply"
                        />
                      ))}

                    <div className="align-self-end mt-5 w-100 text-center ">
                      {card.shortDisclaimer && (
                        <p className="text-gray body-text-sm mb-1 w-100">{card.shortDisclaimer}</p>
                      )}
                      {card.termsConditions && (
                        <a
                          href={card.termsConditions}
                          className="text-decoration-underline-hover  body-text-sm">
                          Rates and fees
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="border-top mt-4 d-lg-flex">
                  <div className="py-3 py-lg-4 brd-dyn pe-lg-4">
                    <p className="body-text-sm text-gray mb-2 text-center">
                      Recommended FICO<sup>®</sup> Score<sup>Θ</sup>
                    </p>
                    <FicoScoreRange
                      className="mx-auto"
                      scores={card.creditRange}
                      features={card.features}
                    />
                  </div>
                  <div className="pt-3 pb-2 pt-lg-4 pb-lg-3 ps-lg-4">
                    <table>
                      <tbody>
                        <tr>
                          <th className="align-top">
                            <p className="h7 mb-2">{DescrCategories.annualFee}:</p>
                          </th>
                          <td className="ps-3">
                            <p className="mb-2 body-text-sm">
                              {parse(returnBulletValue(card.annualFee))}
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <th className="align-top">
                            <p className="h7 mb-2">{DescrCategories.introApr}:</p>
                          </th>
                          <td className="ps-3">
                            <p className="mb-2 body-text-sm">
                              {parse(returnBulletValue(card.introApr))}
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <th className="align-top">
                            <p className="h7 mb-2">{DescrCategories.standardApr}:</p>
                          </th>
                          <td className="ps-3">
                            <p className="mb-2 body-text-sm">
                              {parse(returnBulletValue(card.standardApr))}
                            </p>
                          </td>
                        </tr>

                        <tr>
                          <th className="align-top">
                            <p className="h7 mb-2">{DescrCategories.ongoingBTapr}:</p>
                          </th>
                          <td className="ps-3">
                            <p className="mb-2 body-text-sm">
                              {parse(returnBulletValue(card.balanceTransferApr))}
                            </p>
                          </td>
                        </tr>
                        {card.introBonus && (
                          <tr>
                            <th className="align-top">
                              <p className="h7 mb-2">{DescrCategories.introBonus}:</p>
                            </th>
                            <td className="ps-3">
                              <p className="mb-2 body-text-sm">{parse(card.introBonus)}</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Accordion
                  flush
                  alwaysOpen
                  className="accordion-angle-icon">
                  <Accordion.Item
                    className="border-top"
                    eventKey="0-det-b">
                    <Accordion.Button
                      aria-controls="0-det-b"
                      className="text-gray h7 mb-0 py-lg-4 ps-0"
                      id="0-det-h">
                      Card details
                    </Accordion.Button>
                    <Accordion.Body
                      id="0-det-b"
                      role="region"
                      className="body-text-sm"
                      aria-describedby="0-det-h">
                      {card?.bullets?.length && (
                        <ul>
                          {card.bullets.map((bullet, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={`${i}-0-det-bul`}>{parse(superscriptRegisterMark(bullet))}</li>
                          ))}
                        </ul>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="1-pc-b">
                    <Accordion.Button
                      aria-controls="1-pc-b"
                      id="1-pc-h"
                      className="angle-icon text-gray h7 mb-0 py-lg-4  ps-0">
                      Pros and cons
                    </Accordion.Button>
                    <Accordion.Body
                      id="1-pc-b"
                      role="region"
                      aria-describedby="1-pc-h">
                      <div className="d-lg-flex justify-content-lg-between">
                        {card.pros && card.pros.length && (
                          <ul className="list-unstyled mb-lg-0 col-lg-6 pe-lg-4">
                            {card.pros.map((pros, i) => (
                              <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${i}-1-det-pros`}
                                className={`${
                                  i === card.pros.length - 1 ? 'mb-lg-0' : ''
                                } d-flex align-items-top`}>
                                <IconsHolder
                                  className="me-3"
                                  size="xs"
                                  shape="round"
                                  icon="ecs-font-icon-act-check"
                                  variant="success"
                                />
                                <p className="mb-0 body-text-sm">
                                  {parse(superscriptRegisterMark(pros))}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                        {card.cons && card.cons.length && (
                          <ul className="list-unstyled mb-0 col-lg-6 ps-lg-4">
                            {card.cons.map((cons, i) => (
                              <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${i}-1-det-cons`}
                                className={`${
                                  i === card.cons.length - 1 ? 'mb-0' : ''
                                } d-flex align-items-top`}>
                                <IconsHolder
                                  className="me-3"
                                  size="xs"
                                  shape="round"
                                  icon="ecs-font-icon-act-close"
                                  variant="danger"
                                />
                                <p className="mb-0 body-text-sm">
                                  {parse(superscriptRegisterMark(cons))}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
                {card.provider && card.providerPath && (
                  <Link
                    className="pt-3 pt-lg-4 border-top d-block text-decoration-underline-hover body-text-sm"
                    to={card.providerPath}>
                    See all {addApostropheS(card.provider)} offers
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <div
        className="container container-py border-bottom"
        id="single-review">
        {!card.suppressedViews.includes(suppressionValues.public) &&
          card.reviews &&
          card.reviews.average_rating && (
            <>
              <h3>Review Snapshot</h3>
              <p className="mb-0 pb-5 border-bottom">
                Opinions are provided by Experian users that are actual credit card customers and do
                not reflect the views of Experian or the lender.
              </p>
              <PRsingleReview offerId={card.offerId} />
            </>
          )}
        <div
          className={`col-12 disclosures ${
            card.reviews && card.reviews.average_rating ? 'disclosures-mt' : ''
          }`}>
          <p>{parse(opinionsReviewsDisclosure)}</p>
          <p>{parse(prosConsDisclosure)}</p>
          <p>{parse(completeInfo)}</p>
          <p>{parse(advertiserDisclosure)}</p>
          <DisclosureWithSymbol
            disclosure={ficoShort}
            symbol="Θ"
          />
        </div>
      </div>
      {allArticles && allArticles.length === 6 && (
        <section>
          <div className="container overflow-hidden container-py">
            <h2 className="eyebrow-text heading-mb text-gray">Learn more about credit cards</h2>
            <ArticlesSlider content={allArticles} />
          </div>
        </section>
      )}

      <CardsSuppressedModal suppressed={card.suppressedViews.includes(suppressionValues.public)} />

      <Script
        id="pr-reviews-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
                "@context": "http://schema.org/",
                "@type": "Product",
                "brand": "${card.provider}",
                "category": "Credit Card",
                "image": "${imageWithPrefix(card.imageDownloaded)}",
                "name": "${card.offerName}",
                "description": "View details for the ${card.offerName} from ${card.provider}.",
                "aggregateRating": {
                    "@type": "aggregateRating",
                    "ratingValue": "${card.reviews?.average_rating || 0}",
                    "bestRating": "5",
                    "reviewCount": "${card.reviews?.review_count || 0}"
                }
            }`
        }}
      />
    </LayoutFull>
  );
}
export const query = graphql`
  query CardDetailsTemplate($offerId: String) {
    allCards(filter: { offerId: { eq: $offerId } }) {
      nodes {
        offerId
        offerName
        balanceTransferApr
        offerNameSuperscripted
        rewards
        features
        creditRange
        annualFee
        introApr
        link
        provider
        providerPath
        imageDownloaded
        shortDisclaimer
        rewardsContent {
          type
          value
        }
        rewardsTooltip
        standardApr
        termsConditions
        introBonus
        path
        reviews {
          average_rating
          review_count
          id
        }
        bullets
        pros
        cons
        suppressedViews
      }
    }
    allArticles(limit: 6, filter: { identifier: { eq: "841" } }) {
      nodes {
        ...ArticleFields
      }
    }
  }
`;
export default CardDetailsTemplate;
export function Head({ data }: PageProps<CardDetailsTemplateProps, pageContextType>) {
  const card = data.allCards.nodes[0];
  return (
    <>
      <body
        data-op="EXP-CRD-DET"
        data-section="ecom-creditmatch"
        data-category="credit-cards"
        className={card.suppressedViews.includes(suppressionValues.public) ? 'suppressed' : ''}
      />
      <SEO
        title={`${card.offerName} - Experian`}
        description={`View offer details for ${card.offerName}. See your best credit card matches when you sign up for Experian.`}
        socialImage="/social/social-credit-cards.webp">
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <link
          rel="preload"
          as="image"
          href={imageWithPrefix(`${card.imageDownloaded}`)}
        />
      </SEO>
    </>
  );
}
